"use client";

import { useTranslations } from "next-intl";
import type { FontOption, Palette } from "./data";
import { loc, sortPalette } from "./helpers";
import { DesktopPreview } from "./DesktopPreview";
import { MobilePreview } from "./MobilePreview";
import { ColorEditor } from "./ColorEditor";

export function PreviewSidebar({
  locale,
  viewMode,
  setViewMode,
  customColors,
  selectedFont,
  selectedPalette,
  handleColorChange,
  handleReverse,
  setCustomColors,
}: {
  locale: string;
  viewMode: "desktop" | "mobile";
  setViewMode: (v: "desktop" | "mobile") => void;
  customColors: string[];
  selectedFont: FontOption;
  selectedPalette: Palette;
  handleColorChange: (idx: number, hex: string) => void;
  handleReverse: () => void;
  setCustomColors: (c: string[]) => void;
}) {
  const t = useTranslations("catalog");

  return (
    <div>
      {/* header row */}
      <div className="mb-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className="h-2 w-2 animate-pulse rounded-full"
            style={{ background: "#34D399" }}
          />
          <p className="text-xs font-medium" style={{ color: "#8B8B94" }}>
            {t("livePreview")}
          </p>
        </div>
        <div
          className="flex gap-1 rounded-lg p-0.5"
          style={{ background: "rgba(245,245,247,0.06)" }}
        >
          <button
            onClick={() => setViewMode("desktop")}
            className="rounded-md px-2.5 py-1 text-[10px] font-semibold transition-all"
            style={{
              background:
                viewMode === "desktop" ? "rgba(30,63,232,0.5)" : "transparent",
              color: viewMode === "desktop" ? "#fff" : "#8B8B94",
            }}
          >
            {t("viewDesktop")}
          </button>
          <button
            onClick={() => setViewMode("mobile")}
            className="rounded-md px-2.5 py-1 text-[10px] font-semibold transition-all"
            style={{
              background:
                viewMode === "mobile" ? "rgba(30,63,232,0.5)" : "transparent",
              color: viewMode === "mobile" ? "#fff" : "#8B8B94",
            }}
          >
            {t("viewMobile")}
          </button>
        </div>
      </div>
      {/* palette + font label */}
      <p className="mb-3 truncate text-xs" style={{ color: "#5A5A64" }}>
        <span style={{ color: "#8B8B94" }}>
          {loc(selectedPalette.name, locale)}
        </span>
        {" · "}
        <span>{selectedFont.name}</span>
      </p>
      {/* preview */}
      <div className="mb-4">
        {viewMode === "desktop" ? (
          <DesktopPreview colors={customColors} font={selectedFont} />
        ) : (
          <MobilePreview colors={customColors} font={selectedFont} />
        )}
      </div>
      {/* color editor */}
      <ColorEditor
        colors={customColors}
        originalColors={selectedPalette.colors}
        paletteName={loc(selectedPalette.name, locale)}
        onChange={handleColorChange}
        onReset={() => setCustomColors(sortPalette(selectedPalette.colors))}
        onReverse={handleReverse}
      />
    </div>
  );
}
